import React from "react";
import {
    Card, ListGroup, Badge, Button, Alert,
    Form, InputGroup, ProgressBar
} from "react-bootstrap";
import { Link } from "react-router-dom";

export default function ResultatScan({
    scanResult,
    loading,
    error,
    alertes,
    mode,
    quantite,
    setQuantite,
    commentaire,
    setCommentaire,
    onEntree,
    onSortie,
    onVoirProduit,
    getStockStatusColor,
    getStockProgress,
    formatCurrency
}) {
    if (loading) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                    <h5>Recherche en cours...</h5>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-5">
                    <div className="mb-3">
                        <i className="ri-error-warning-line text-danger" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h5 className="text-danger">Erreur</h5>
                    <p className="text-muted">{error}</p>
                    <div className="mt-3">
                        <Button variant="outline-primary" onClick={() => window.location.reload()}>
                            <i className="ri-refresh-line me-2"></i>
                            Réessayer
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (!scanResult) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="text-center py-5">
                    <div className="mb-3">
                        <i className="ri-qr-code-line text-muted" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5>Aucun produit scanné</h5>
                    <p className="text-muted">
                        Utilisez la caméra ou la saisie manuelle pour rechercher un produit
                    </p>
                    <div className="mt-3">
                        <Badge bg="light" text="dark" className="p-2 me-2">
                            <i className="ri-camera-line me-1"></i>
                            Caméra
                        </Badge>
                        <Badge bg="light" text="dark" className="p-2">
                            <i className="ri-keyboard-line me-1"></i>
                            Manuel
                        </Badge>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 pt-4">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="ri-checkbox-circle-line text-success me-2"></i>
                        Produit trouvé
                    </h5>
                    <Badge 
                        bg={getStockStatusColor(scanResult.stock_actuel, scanResult.seuil_alerte)}
                        className="px-3 py-2"
                    >
                        <i className="ri-stack-line me-1"></i>
                        Stock: {scanResult.stock_actuel || 0}
                    </Badge>
                </div>
            </Card.Header>
            <Card.Body>
                {/* Alertes */}
                {alertes.map((alerte, index) => (
                    <Alert key={index} variant={alerte.type} className="mb-3">
                        <i className={`ri-${alerte.type === 'danger' ? 'alert' : 'information'}-line me-2`}></i>
                        {alerte.message}
                    </Alert>
                ))}

                {/* Infos produit */}
                <ListGroup variant="flush" className="mb-3">
                    <ListGroup.Item className="px-0">
                        <small className="text-muted d-block">Désignation</small>
                        <strong>{scanResult.designation}</strong>
                    </ListGroup.Item>
                    
                    <ListGroup.Item className="px-0">
                        <small className="text-muted d-block">Référence</small>
                        <code>{scanResult.reference}</code>
                    </ListGroup.Item>

                    {scanResult.code_barre && (
                        <ListGroup.Item className="px-0">
                            <small className="text-muted d-block">Code-barres</small>
                            <code>{scanResult.code_barre}</code>
                        </ListGroup.Item>
                    )}

                    <ListGroup.Item className="px-0">
                        <div className="row">
                            <div className="col-6">
                                <small className="text-muted d-block">Catégorie</small>
                                <span>{scanResult.nom_categorie || 'Non catégorisé'}</span>
                            </div>
                            <div className="col-6">
                                <small className="text-muted d-block">Emplacement</small>
                                <span>{scanResult.emplacement || 'Non défini'}</span>
                            </div>
                        </div>
                    </ListGroup.Item>

                    <ListGroup.Item className="px-0">
                        <div className="row">
                            <div className="col-6">
                                <small className="text-muted d-block">Prix achat</small>
                                <span>{formatCurrency(scanResult.prix_achat_ht)} HT</span>
                            </div>
                            <div className="col-6">
                                <small className="text-muted d-block">Prix vente</small>
                                <span className="text-success">{formatCurrency(scanResult.prix_vente_ht)} HT</span>
                            </div>
                        </div>
                    </ListGroup.Item>

                    <ListGroup.Item className="px-0">
                        <small className="text-muted d-block">Stock</small>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span>Disponible: <strong>{scanResult.stock_actuel || 0}</strong></span>
                            <span className="text-muted">Seuil: {scanResult.seuil_alerte}</span>
                        </div>
                        <ProgressBar 
                            now={getStockProgress(scanResult.stock_actuel, 100)}
                            variant={getStockStatusColor(scanResult.stock_actuel, scanResult.seuil_alerte)}
                            style={{ height: '8px' }}
                        />
                    </ListGroup.Item>
                </ListGroup>

                {/* Actions selon le mode */}
                {mode !== 'recherche' && (
                    <div className="mb-3">
                        <Form.Group className="mb-2">
                            <Form.Label>Quantité</Form.Label>
                            <InputGroup>
                                <Button 
                                    variant="outline-secondary"
                                    onClick={() => setQuantite(Math.max(1, quantite - 1))}
                                >
                                    <i className="ri-subtract-line"></i>
                                </Button>
                                <Form.Control
                                    type="number"
                                    min="1"
                                    value={quantite}
                                    onChange={(e) => setQuantite(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="text-center"
                                />
                                <Button 
                                    variant="outline-secondary"
                                    onClick={() => setQuantite(quantite + 1)}
                                >
                                    <i className="ri-add-line"></i>
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Commentaire (optionnel)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ex: Réception fournisseur, vente, etc."
                                value={commentaire}
                                onChange={(e) => setCommentaire(e.target.value)}
                            />
                        </Form.Group>
                    </div>
                )}

                {/* Boutons d'action */}
                <div className="d-grid gap-2">
                    {mode === 'entree' && (
                        <Button 
                            variant="success" 
                            size="lg"
                            onClick={onEntree}
                            disabled={!quantite || quantite <= 0}
                        >
                            <i className="ri-add-circle-line me-2"></i>
                            Confirmer l'entrée ({quantite})
                        </Button>
                    )}

                    {mode === 'sortie' && (
                        <Button 
                            variant="danger" 
                            size="lg"
                            onClick={onSortie}
                            disabled={!quantite || quantite <= 0 || quantite > (scanResult.stock_actuel || 0)}
                        >
                            <i className="ri-subtract-line me-2"></i>
                            Confirmer la sortie ({quantite})
                        </Button>
                    )}

                    <Button 
                        variant="outline-primary"
                        onClick={onVoirProduit}
                    >
                        <i className="ri-file-list-line me-2"></i>
                        Voir détails du produit
                    </Button>
                </div>

                {/* Derniers mouvements */}
                {scanResult.derniers_mouvements && scanResult.derniers_mouvements.length > 0 && (
                    <div className="mt-4">
                        <small className="text-muted d-block mb-2">Derniers mouvements :</small>
                        {scanResult.derniers_mouvements.map((mvt, index) => (
                            <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                                <div>
                                    <Badge bg={mvt.type_mouvement === 'entree' ? 'success' : 'danger'}>
                                        {mvt.type_mouvement === 'entree' ? '+' : '-'}{mvt.quantite}
                                    </Badge>
                                    <small className="ms-2 text-muted">{new Date(mvt.date_mouvement).toLocaleDateString()}</small>
                                </div>
                                <small>{mvt.commentaire}</small>
                            </div>
                        ))}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}